import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SpotlightProvider, openSpotlight } from "@mantine/spotlight";
import { Modal, Stack, Button, Avatar } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconSearch,
  IconArmchair,
  IconParking,
  IconPresentation,
  IconDoorEnter,
  IconDoorExit,
  IconBeach,
  IconCalendar,
  IconCalendarEvent,
} from "@tabler/icons";
import moment from "moment";
import * as apiService from "../api-service";
import { getEmployeesCached } from "../services/employeesCache";
import { getAllEventsCached } from "../services/eventsCache";
import { namesLikelyMatch } from "./Extensions";
import { colorFromString } from "../utils/color";

// Mirrors booker.js's own isOnDay exactly - a booking "covers" a given day if that day falls
// anywhere within its start/end range, both ends included.
const isOnDay = (d, day) =>
  moment(day).isBetween(moment(d.data().date_start.toDate()), moment(d.data().date_end.toDate()), "day", "[]");

// Mirrors booker.js's own getHalfDayPeriod exactly, so a half-day desk booking shows the same
// AM/PM label here as it does on the Booker board itself.
const getHalfDayPeriod = (start, end) => {
  const startUtc = moment.utc(start);
  const endUtc = moment.utc(end);
  const startOfDay = startUtc.clone().startOf("day");
  const noon = startOfDay.clone().add(12, "hours");
  const nextMidnight = startOfDay.clone().add(24, "hours");
  const endsAtDayBoundary = endUtc.isSame(nextMidnight) || endUtc.isSame(nextMidnight.clone().subtract(1, "second"));
  if (startUtc.isSame(startOfDay) && endUtc.isSame(noon)) return "AM";
  if (startUtc.isSame(noon) && endsAtDayBoundary) return "PM";
  return null;
};

const getInitials = (name) =>
  (name ?? "")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Everywhere someone could be booked on a given day, in the same order Booker's own board shows
// its categories - desk, parking, meeting, office duty, then Away/Off. A person can show up more
// than once (e.g. a desk in the morning plus a meeting room slot in the afternoon), so this
// always returns every match rather than stopping at the first.
function findBookingsForEmployee(employee, dayEvents, awayEvents, rooms, parkingSpaces) {
  const results = [];

  dayEvents
    .filter((d) => d.data().type === "desk" && d.data().created_by === employee.uid)
    // state 0/2 are cancelled/rejected bookings - matches Booker's own filter.
    .filter((d) => d.data()?.state !== 2 && d.data()?.state !== 0)
    .forEach((d) => {
      const room = rooms.find((r) => r.id === d.data().room);
      const half = getHalfDayPeriod(d.data().date_start.toDate(), d.data().date_end.toDate());
      results.push({
        icon: IconArmchair,
        label: `Desk - ${room?.title ?? "Unknown Location"}${half ? ` (${half})` : ""}`,
      });
    });

  dayEvents
    .filter((d) => d.data().type === "parking" && d.data().created_by === employee.uid)
    .forEach((d) => {
      const space = parkingSpaces.find((p) => p.id === d.data().space);
      results.push({ icon: IconParking, label: `Parking - ${space?.title ?? "Unknown Space"}` });
    });

  dayEvents
    .filter((d) => d.data().type === "meeting" && d.data().created_by === employee.uid)
    .forEach((d) => {
      const room = rooms.find((r) => r.id === d.data().room);
      const start = moment(d.data().date_start.toDate()).format("HH:mm");
      const end = moment(d.data().date_end.toDate()).format("HH:mm");
      results.push({ icon: IconPresentation, label: `Meeting - ${room?.title ?? "Unknown Location"} (${start}-${end})` });
    });

  dayEvents
    .filter((d) => d.data().type === "office" && d.data().created_by === employee.uid)
    .forEach((d) => {
      const isOpen = d.data().office_type === "open";
      results.push({ icon: isOpen ? IconDoorEnter : IconDoorExit, label: isOpen ? "Opening the office" : "Closing the office" });
    });

  // Away/Off comes from an external iCal feed (not a Firestore event), matched by name - the same
  // fuzzy match Booker's own conflict checks use, since the feed's own name formatting doesn't
  // always match the portal display name exactly (see away_name on Edit User).
  const awayName = employee.customClaims?.["away_name"] || employee.displayName || "";
  awayEvents
    .filter((d) => namesLikelyMatch(d.data().title, awayName))
    .forEach((d) => {
      results.push({ icon: IconBeach, label: d.data().status ? `Away/Off (${d.data().status})` : "Away/Off" });
    });

  return results;
}

// A global "Find Someone" command palette (Ctrl/Cmd+K from anywhere in the app) that answers
// "where is X today" without opening Booker and clicking through the calendar - see the header
// search icon in App.js for the other way in. Wraps the whole signed-in app (children), same
// pattern as MantineProvider/Router - any descendant can open it via openSpotlight() without
// needing to be passed anything, since @mantine/spotlight's open/close functions work through a
// shared event bus rather than through React context.
export default function FindSomeoneSpotlight({ companyId, companyData, userClaims, currentUser, userToken, children }) {
  const navigate = useNavigate();
  const isSignedIn = !!(companyId && currentUser && userClaims);

  const [targetDate, setTargetDate] = useState(() => moment().startOf("day").toDate());
  const [employees, setEmployees] = useState([]);
  const [events, setEvents] = useState([]);
  const [awayEvents, setAwayEvents] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState(new Date());

  // Controlled (rather than left to SpotlightProvider's own internal state) purely so
  // peopleActions below can tell whether anyone's actually searched for a name yet - see hasQuery.
  const [query, setQuery] = useState("");
  const hasQuery = query.trim().length > 0;

  const rooms = useMemo(() => companyData?.["rooms"] ?? [], [companyData]);
  const parkingSpaces = useMemo(() => companyData?.["parking_spaces"] ?? [], [companyData]);

  // Fetched lazily on first open (not on every page load) - employees, every booking event
  // (getAllEvents' own 2-year lookback already covers any date this feature would realistically
  // be asked about), and the Away/Off feed. Loaded once per session rather than on every open,
  // same "good enough, refresh on demand" tradeoff Booker itself makes.
  const loadData = useCallback(() => {
    if (!isSignedIn || isLoadingData || hasLoadedOnce) return;
    setIsLoadingData(true);
    Promise.all([
      getEmployeesCached({ userIdToken: userToken, companyId }),
      getAllEventsCached(companyId).then((snapshot) => snapshot.docs ?? []),
      apiService.getAwayEvents({ userIdToken: userToken, companyId }).catch(() => ({ events: [] })),
    ])
      .then(([employeeList, nativeEvents, awayData]) => {
        setEmployees(employeeList ?? []);
        setEvents(nativeEvents);
        // Wrapped to look like a Firestore query doc snapshot (an `id` plus a `.data()`
        // accessor), same trick booker.js's own wrapAwayEvent uses, so findBookingsForEmployee
        // above can treat every event source identically.
        setAwayEvents(
          (awayData?.events ?? []).map((event, i) => ({
            id: `away-${i}`,
            data: () => ({
              type: "away",
              title: event.name,
              status: event.status,
              date_start: { toDate: () => new Date(event.start) },
              date_end: { toDate: () => new Date(event.end) },
            }),
          }))
        );
        setHasLoadedOnce(true);
      })
      .catch((err) => console.error("FindSomeoneSpotlight failed to load:", err))
      .finally(() => setIsLoadingData(false));
  }, [isSignedIn, isLoadingData, hasLoadedOnce, userToken, companyId]);

  const dayEvents = useMemo(() => events.filter((d) => isOnDay(d, targetDate)), [events, targetDate]);
  const dayAwayEvents = useMemo(() => awayEvents.filter((d) => isOnDay(d, targetDate)), [awayEvents, targetDate]);

  // Reused both as each result's subtitle (below) and folded into the search placeholder further
  // down, so the two can never end up describing the target day differently from each other.
  const dateLabel = moment(targetDate).calendar(null, {
    sameDay: "[Today]",
    nextDay: "[Tomorrow]",
    lastDay: "[Yesterday]",
    nextWeek: "dddd D MMM",
    lastWeek: "dddd D MMM",
    sameElse: "dddd D MMM",
  });

  const peopleActions = useMemo(() => {
    return [...employees]
      .sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? ""))
      .map((employee) => {
        const bookings = findBookingsForEmployee(employee, dayEvents, dayAwayEvents, rooms, parkingSpaces);
        const summary = bookings.length > 0 ? bookings.map((b) => b.label).join("  ·  ") : "No bookings found for this day";
        const avatarColor = colorFromString(employee.displayName ?? employee.uid);
        const displayName = employee.displayName ?? employee.email ?? "Unknown";
        return {
          id: `person-${employee.uid}`,
          // Before anyone's typed a name, this is just a name-ordered list to browse, so the name
          // itself is the big/prominent text. Once there's an actual search query, whoever it
          // matched is already established (their name is right there in the search box) - the
          // answer (where they are) is what's actually being scanned for at that point, so it
          // swaps to being the big text instead, with the name + date as confirmation underneath.
          title: hasQuery ? summary : displayName,
          description: hasQuery ? `${displayName} · ${dateLabel}` : summary,
          group: "People",
          keywords: employee.email,
          icon: (
            <Avatar size={28} radius="xl" color={avatarColor}>
              {getInitials(employee.displayName)}
            </Avatar>
          ),
          // Lands Booker directly on the day being searched (see the /booker/:date route and
          // useParams() handling in booker.js) - the answer itself is already shown above, in the
          // search results, so this is really just "see full context" rather than the primary way
          // of using this.
          onTrigger: () => navigate(`/booker/${moment(targetDate).format("YYYY-MM-DD")}`),
        };
      });
  }, [employees, dayEvents, dayAwayEvents, rooms, parkingSpaces, dateLabel, targetDate, hasQuery, navigate]);

  // Always-visible quick actions for changing which day the People results above are for -
  // closeOnTrigger: false on the relative-day ones keeps the palette open so the change is
  // visible immediately and you can carry straight on typing a name afterwards.
  const dateActions = useMemo(
    () => [
      {
        id: "date-today",
        title: "Today",
        description: moment().format("dddd, D MMMM YYYY"),
        group: "Jump to Date",
        icon: <IconCalendar size={18} />,
        closeOnTrigger: false,
        onTrigger: () => setTargetDate(moment().startOf("day").toDate()),
      },
      {
        id: "date-tomorrow",
        title: "Tomorrow",
        description: moment().add(1, "day").format("dddd, D MMMM YYYY"),
        group: "Jump to Date",
        icon: <IconCalendar size={18} />,
        closeOnTrigger: false,
        onTrigger: () => setTargetDate(moment().add(1, "day").startOf("day").toDate()),
      },
      {
        id: "date-pick",
        title: "Pick a Date...",
        description: "Check a specific day instead of today",
        group: "Jump to Date",
        icon: <IconCalendarEvent size={18} />,
        onTrigger: () => {
          setPickerDate(targetDate);
          setPickerOpen(true);
        },
      },
    ],
    [targetDate]
  );

  const loadingAction = {
    id: "loading",
    title: isLoadingData ? "Loading employees..." : "Start typing to search",
    description: "",
    group: "People",
    closeOnTrigger: false,
    onTrigger: () => {},
  };

  const actions = [...dateActions, ...(hasLoadedOnce ? peopleActions : [loadingAction])];

  return (
    <SpotlightProvider
      actions={actions}
      onSpotlightOpen={loadData}
      // Controlled (see the query state above) so peopleActions can tell whether anyone's actually
      // typed a search yet - cleared on close so reopening always starts back in "browse everyone
      // by name" mode rather than remembering the last search.
      query={query}
      onQueryChange={setQuery}
      onSpotlightClose={() => setQuery("")}
      // "Showing <date>" is folded into the placeholder (rather than a fake disabled action) so
      // it's always visible without cluttering the results list - it just disappears once you
      // start typing, by which point you've already seen it. dateLabel is capitalised for use as
      // each result's subtitle above, so it's lowercased again here to read naturally mid-sentence.
      searchPlaceholder={`Search employees - showing ${dateLabel.toLowerCase()}...`}
      searchIcon={<IconSearch size={18} />}
      shortcut="mod + K"
      nothingFoundMessage="No employees match that search."
      highlightQuery
      limit={30}
      disabled={!isSignedIn}
      overlayProps={{ opacity: 0.55, blur: 3 }}
    >
      {children}

      <Modal opened={pickerOpen} onClose={() => setPickerOpen(false)} title="Pick a Date" centered>
        <Stack spacing="sm">
          <DatePickerInput
            label="Date"
            valueFormat="DD MMMM YYYY"
            value={pickerDate}
            onChange={setPickerDate}
            dropdownType="modal"
            popoverProps={{ zIndex: 1000000 }}
          />
          <Button
            onClick={() => {
              setTargetDate(moment(pickerDate ?? new Date()).startOf("day").toDate());
              setPickerOpen(false);
              // Re-opens the palette (rather than leaving it closed) so the results for the newly
              // picked date are immediately visible, same as the relative-day quick actions above.
              setTimeout(() => openSpotlight(), 0);
            }}
          >
            Show
          </Button>
        </Stack>
      </Modal>
    </SpotlightProvider>
  );
}
